import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MediaCard from "@/components/feature/MediaCard";
import ArticleListItem from "@/components/feature/ArticleListItem";
import { usePageMeta } from "@/hooks/usePageMeta";
import { findSpecialById, getRelatedContent, isMedia } from "@/lib/content";
import { specialList } from "@/mocks/specials";
import type { MediaItem } from "@/mocks/media";
import type { ArticleItem } from "@/mocks/articles";

export default function SpecialPage() {
  const { id = "" } = useParams();
  const special = findSpecialById(id);

  usePageMeta(
    special ? `${special.title} - 蜜桃传媒` : "影视专题 - 蜜桃传媒",
    special
      ? `${special.subtitle}。蜜桃传媒精选专题「${special.title}」，为你汇集相关影视作品与资讯。`
      : "蜜桃传媒精选影视专题，按主题深度浏览影视作品与资讯。"
  );

  if (!special) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6">
        <Breadcrumb items={[{ label: "精选专题" }]} />
        <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-10 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-black text-foreground-950">内容暂不可用</h1>
          <p className="mt-2 text-sm text-foreground-600">该专题可能已下线，欢迎浏览其他精选专题。</p>
          <Link
            to="/"
            className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const members = getRelatedContent(special.memberIds, special.memberIds.length);
  const mediaMembers = members.filter(isMedia) as MediaItem[];
  const articleMembers = members.filter((item) => !isMedia(item)) as ArticleItem[];
  const relatedSpecials = specialList.filter((item) => item.id !== special.id).slice(0, 3);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "精选专题", to: "/#specials" }, { label: special.title }]} />

      <header className="relative mt-4 h-[280px] w-full overflow-hidden rounded-lg md:h-[380px]">
        <img
          src={special.cover}
          alt={`${special.title}专题主图`}
          title={`${special.title}精选专题`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/45 to-foreground-950/10"></div>
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
          <span className="mb-2 w-fit rounded-full bg-accent-500 px-3 py-1 text-[11px] font-bold text-background-50">
            精选专题
          </span>
          <h1 className="font-heading text-2xl font-black text-background-50 md:text-4xl">
            {special.title}
          </h1>
          <p className="mt-2 text-sm font-medium text-primary-200 md:text-base">
            {special.subtitle}
          </p>
        </div>
      </header>

      <section className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
        <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
          <i className="ri-quill-pen-line text-primary-600"></i>
          专题导语
        </h2>
        <p className="mt-3 text-sm leading-loose text-foreground-700 md:text-base">
          {special.intro}
        </p>
      </section>

      {mediaMembers.length > 0 && (
        <section className="mt-10" aria-label="专题影视作品">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-film-line text-xl"></i>
            </span>
            <h2 className="font-heading text-lg font-black text-foreground-950 md:text-xl">
              专题影视作品
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {mediaMembers.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {articleMembers.length > 0 && (
        <section className="mt-10" aria-label="专题相关资讯">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-article-line text-xl"></i>
            </span>
            <h2 className="font-heading text-lg font-black text-foreground-950 md:text-xl">
              专题相关资讯
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {articleMembers.map((item) => (
              <ArticleListItem key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {relatedSpecials.length > 0 && (
        <section className="mt-12" aria-label="相关专题推荐">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-200 text-secondary-900">
              <i className="ri-bookmark-3-line text-xl"></i>
            </span>
            <h2 className="font-heading text-lg font-black text-foreground-950 md:text-xl">
              相关专题推荐
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {relatedSpecials.map((item) => (
              <Link
                key={item.id}
                to={`/special/${item.id}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-secondary-400"
              >
                <div className="relative h-36 w-full overflow-hidden bg-background-100">
                  <img
                    src={item.cover}
                    alt={`${item.title}专题封面`}
                    title={`${item.title}精选专题`}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-transparent"></div>
                  <h3 className="absolute bottom-3 left-4 right-4 font-heading text-base font-black text-background-50">
                    {item.title}
                  </h3>
                </div>
                <p className="p-4 text-xs leading-relaxed text-foreground-600">{item.subtitle}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}